import { useEffect, useState } from "react";
import type { Track } from "livekit-client";
import { Maximize2, MicOff, MonitorUp } from "lucide-react";
import { Avatar, AvatarFallback, Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger, cn } from "@link/ui";

// Presentation only. Attaching a second video element never starts capture,
// subscribes to another room, or changes encryption, audio, or call ownership.
export type VideoTrack = Pick<Track, "attach" | "detach">;

function TrackVideo({ track, label, screenShare }: { track: VideoTrack; label: string; screenShare: boolean }) {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  useEffect(() => {
    if (!element) return;
    track.attach(element);
    return () => { track.detach(element); };
  }, [element, track]);
  // Audio remains in the existing RemoteAudio renderer, never this preview.
  return <video ref={setElement} autoPlay playsInline muted aria-label={label}
    className={cn("h-full w-full", screenShare ? "object-contain" : "object-cover")} />;
}

export function CallVideoTile({ track, label, screenShare, speaking, muted }: {
  track: VideoTrack | null; label: string; screenShare: boolean; speaking: boolean; muted: boolean;
}) {
  const [expandedTrack, setExpandedTrack] = useState<VideoTrack | null>(null);
  const expandable = screenShare && track !== null;
  const expanded = expandable && expandedTrack === track;
  useEffect(() => {
    if (!expandable || expandedTrack !== track) setExpandedTrack(null);
  }, [expandable, expandedTrack, track]);

  return <Dialog open={expanded} onOpenChange={open => setExpandedTrack(open && expandable ? track : null)}>
    <div className={cn("relative aspect-video overflow-hidden rounded-xl bg-black ring-2 ring-white/10 transition-[box-shadow] duration-150", speaking && "ring-4 ring-emerald-400")}
      data-speaking={speaking ? "true" : "false"} data-muted={muted ? "true" : "false"} data-screen-share={screenShare}>
      {track ? <TrackVideo track={track} label={`${label}${screenShare ? "’s screen" : "’s camera"}`} screenShare={screenShare} />
        : <div className="flex h-full items-center justify-center"><Avatar className="h-16 w-16"><AvatarFallback>{label.slice(0, 2).toUpperCase()}</AvatarFallback></Avatar></div>}
      <span className="pointer-events-none absolute bottom-2 left-2 max-w-[calc(100%-3rem)] truncate rounded-md bg-black/65 px-2 py-1 text-xs font-semibold text-white">
        {label}{screenShare ? " · Screen share" : ""}
      </span>
      {muted && <span className="pointer-events-none absolute bottom-2 right-2 flex h-7 w-7 items-center justify-center rounded-full bg-rose-600 text-white" aria-label={`${label} is muted`}><MicOff className="h-3.5 w-3.5" /></span>}
      {expandable && <DialogTrigger asChild>
        <button type="button" className="screen-share-expand absolute inset-0 cursor-zoom-in rounded-xl text-white focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4 focus-visible:outline-white" aria-label={`Expand ${label}’s screen share`} title="Expand screen share">
          <span className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-lg bg-black/65"><Maximize2 className="h-4 w-4" aria-hidden /></span>
        </button>
      </DialogTrigger>}
    </div>
    <DialogContent className="screen-share-viewer">
      <div className="screen-share-viewer-heading">
        <MonitorUp size={20} aria-hidden />
        <div className="min-w-0"><DialogTitle className="truncate text-base">{label}’s screen</DialogTitle>
          <DialogDescription className="text-xs">Live screen share · Your call is still connected</DialogDescription></div>
      </div>
      <div className="screen-share-viewer-video">
        {expanded && track && <TrackVideo track={track} label={`${label}’s expanded screen share`} screenShare />}
      </div>
      <div className="screen-share-viewer-footer"><span>Press Esc to return to the call</span>
        <DialogClose asChild><button type="button" className="rounded-lg border border-cloud-300 px-4 py-2 text-sm font-semibold focus-visible:ring-2 focus-visible:ring-strawberry-400">Back to call</button></DialogClose>
      </div>
    </DialogContent>
  </Dialog>;
}
